import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Logo from "../assets/dexton-logo-png.png";

const SCROLL_THRESHOLD = 90;
const EXIT_MS = 520;

const MARQUEE_ITEMS = [
  "Custom Software",
  "React & Next.js",
  "Mobile Apps",
  "AI Features",
  "DevOps",
  "SaaS MVP",
  "Remote Squads",
  "Global delivery",
];

export default function SplashScreen({ onComplete }) {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const accumulatedRef = useRef(0);
  const dismissingRef = useRef(false);

  const dismiss = useCallback(() => {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    setProgress(100);
    setIsVisible(false);
    window.setTimeout(() => {
      onComplete?.();
    }, EXIT_MS);
  }, [onComplete]);

  useEffect(() => {
    if (typeof window === "undefined" || !isVisible) return undefined;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setReducedMotion(prefersReduced);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const bumpProgress = () => {
      setProgress(Math.min(100, (accumulatedRef.current / SCROLL_THRESHOLD) * 100));
    };

    const tryDismiss = () => {
      if (accumulatedRef.current >= SCROLL_THRESHOLD) {
        dismiss();
      }
    };

    const onWheel = (event) => {
      if (event.deltaY <= 0) return;
      accumulatedRef.current += event.deltaY;
      bumpProgress();
      tryDismiss();
    };

    let touchStartY = 0;

    const onTouchStart = (event) => {
      touchStartY = event.touches[0]?.clientY ?? 0;
    };

    const onTouchMove = (event) => {
      const currentY = event.touches[0]?.clientY ?? touchStartY;
      const delta = touchStartY - currentY;
      if (delta <= 0) return;
      touchStartY = currentY;
      accumulatedRef.current += delta;
      bumpProgress();
      tryDismiss();
    };

    const onKeyDown = (event) => {
      if (["ArrowDown", "PageDown", " ", "Enter"].includes(event.key)) {
        if (event.key === " ") event.preventDefault();
        accumulatedRef.current = SCROLL_THRESHOLD;
        bumpProgress();
        tryDismiss();
      }
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [dismiss, isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Welcome to Dexton Technology"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: "-8%" }}
          transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-zinc-950 text-white"
        >
          <div className="pointer-events-none absolute inset-0 bg-noise opacity-60 mix-blend-overlay" aria-hidden />
          <div className="pointer-events-none absolute inset-0 bg-grid-faint opacity-50" aria-hidden />

          <div
            className="pointer-events-none absolute -left-[15%] top-[8%] h-[min(90vw,560px)] w-[min(90vw,560px)] animate-mesh-drift rounded-full bg-primary-500/35 blur-[110px]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -right-[10%] bottom-[5%] h-[min(70vw,480px)] w-[min(70vw,480px)] animate-mesh-drift-slow rounded-full bg-secondary-500/30 blur-[100px]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 h-px w-[min(92vw,680px)] -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent"
            aria-hidden
          />

          <div className="relative flex flex-1 flex-col items-center justify-center px-4 pb-28 pt-16 sm:px-6 sm:pb-32">
            <motion.img
              src={Logo}
              alt="Dexton Technology"
              initial={{ opacity: 0, y: 14, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mb-8 h-16 w-16 object-contain sm:h-20 sm:w-20"
            />

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.45 }}
              className="mb-5 rounded-full border border-white/15 bg-white/[0.06] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-primary-200/90 backdrop-blur-md sm:text-[11px]"
            >
              Remote-first · Worldwide clients
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="font-display relative text-center text-[2.35rem] font-bold leading-[0.95] tracking-tight sm:text-6xl md:text-7xl lg:text-[5.25rem]"
            >
              <span className="block text-white/90">Build what</span>
              <span className="mt-1 block bg-gradient-to-r from-primary-300 via-white to-secondary-300 bg-clip-text text-transparent">
                ships &amp; scales
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.5 }}
              className="mt-5 max-w-lg text-center text-sm leading-relaxed text-zinc-400 sm:text-base md:mt-6"
            >
              Dexton Technology — engineering-led software partner for SaaS, squads, and enterprise builds.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.42, duration: 0.5 }}
              className="mt-8 flex flex-wrap items-center justify-center gap-2 sm:mt-10"
              aria-hidden
            >
              {["24–48h proposals", "Stakeholder-ready demos", "Pragmatic stacks"].map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-medium text-zinc-300 backdrop-blur-sm sm:text-xs"
                >
                  {chip}
                </span>
              ))}
            </motion.div>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-[7.5rem] track-mask opacity-70 sm:bottom-[8.5rem]" aria-hidden>
            <div className="flex w-max animate-marquee gap-8 whitespace-nowrap px-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/25 sm:text-xs">
              {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, index) => (
                <span key={`${item}-${index}`}>{item}</span>
              ))}
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-0 border-t border-white/10 bg-zinc-950/80 px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 backdrop-blur-xl sm:px-6">
            <div className="mx-auto flex max-w-md flex-col items-center gap-4">
              <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-primary-400 via-white to-secondary-400"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                />
              </div>

              {reducedMotion ? (
                <button
                  type="button"
                  onClick={dismiss}
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/[0.08] px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/[0.12] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400"
                >
                  Enter website
                </button>
              ) : (
                <div className="flex flex-col items-center gap-2 text-center">
                  <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className="flex h-10 w-6 items-start justify-center rounded-full border border-white/25 p-1.5"
                    aria-hidden
                  >
                    <ChevronDown className="h-3.5 w-3.5 text-white/70" strokeWidth={2.5} />
                  </motion.div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-zinc-400 sm:text-xs">
                    Scroll to explore
                  </p>
                  <button
                    type="button"
                    onClick={dismiss}
                    className="mt-1 text-xs text-zinc-500 underline-offset-4 transition hover:text-zinc-300 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400"
                  >
                    or tap to enter
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
